import React, { useContext, useEffect, useState, useCallback } from "react";
import { useIntl } from "react-intl";
import Modal from "../ui-components/Modal";
import useCountryAxiosInstance from "../hooks/useCountryAxiosInstance";
import { NotificationsContext } from "../ui-components/Notifications";
import { useHistory } from "react-router-dom";
import CountryContext from "../CountryContext";
import { CheckIcon } from "@heroicons/react/24/outline";
import { ArrowPathIcon, UserIcon } from "@heroicons/react/20/solid";
import moment from "moment";
import { set_worksite_status } from "utils";

export default function StatusWorksite({
  selectedWorksite,
  setSelectedWorksite,
  userData,
  references,
  author,
  freezed
}) {
  const intl = useIntl();
  const history = useHistory();
  const {
    country: { code },
  } = useContext(CountryContext);
  const { push } = useContext(NotificationsContext);
  const countryApiRequest = useCountryAxiosInstance();

  // state
  const [loading, setLoading] = useState(false);
  const [selectedStep, setSelectedStep] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // utilities
  const steps = Object.values(selectedWorksite.cod_worksite_status).map(
    (curr, idx, arr) => {
      const [prev, next] = [arr.slice(0, idx), arr.slice(idx, -1)];
      const isActive = curr.flg_active || next.some((s) => s.flg_active);
      const isCurrent = !isActive && prev.pop()?.flg_active;
      return { ...curr, flg_active: isActive, flg_current: isCurrent };
    }
  );

  const isCurrentStep = (stepName) =>
    steps.find(
      (step, idx) =>
        step.step === stepName &&
        (idx === steps.length - 1 || !steps[idx + 1].flg_active)
    )?.flg_active ?? false;
  const latesStepNotDeactivated =
    selectedWorksite &&
    Object.values(selectedWorksite.cod_worksite_status)
      .filter((i) => i.flg_active && i.date && i.step !== "deactivated")
      .slice(-1)[0];

  const dispatchSelectedStep = async () => {
    if (!selectedStep) return;

    setLoading(true);
    try {
      const payload = set_worksite_status(
        selectedWorksite.cod_worksite_status,
        selectedStep,
        userData
      );
      const result = await countryApiRequest.put(
        `/worksites/${selectedWorksite.id}/update_status`,
        payload
      );
      push({
        title: intl.formatMessage({ id: "worksite_updated_successfully" }),
        type: "success",
      });
      history.push(`/${code}/worksites/${result.id}`);
      setSelectedWorksite(result);
    } catch (error) {
      console.error(error);
      push({
        type: "error",
        title: intl.formatMessage({
          id:
            error.response.data.detail === "NO result found"
              ? "worksite_error_no_result"
              : "worksite_created_error",
        }),
      });
    } finally {
      setLoading(false);
      setSelectedStep(undefined);
    }
  };

  useEffect(() => {
    setIsModalOpen(!!selectedStep);
  }, [selectedStep]);

  return (
    <>
      <h3 className="md:hidden font-semibold mb-3">
        {intl.formatMessage({ id: "worksiteStatus" })}
      </h3>
      <div
        className={`flex flex-col md:flex-row gap-3 ${
          loading || freezed ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        {/*
          TailwindUI component taken from here (panels, customized):
          https://tailwindui.com/components/application-ui/navigation/steps
        */}
        <nav className="flex-1" aria-label="Progress">
          <ol className="divide-y divide-gray-300 md:flex md:divide-y-0 overflow-hidden">
            {steps.map((curr, idx, arr) => {
              if (curr.step === "deactivated" && !curr.flg_active) {
                return <React.Fragment key={idx}></React.Fragment>;
              }

              const name = intl.formatMessage({ id: curr.step });
              const isActive =
                (curr.flg_active && curr.date) ||
                curr.id <= selectedStep?.id ||
                curr.id <= latesStepNotDeactivated?.id;
              const stepNumber = curr.flg_active ? (
                <CheckIcon className="h-4 w-4" aria-hidden="true" />
              ) : curr.id <= selectedStep?.id && loading ? (
                <ArrowPathIcon
                  className="h-4 w-4 animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <span className="text-xl md:hidden">{curr.id}</span>
              );
              const textCls = isActive
                ? "text-white"
                : curr.flg_current && curr.date
                ? "text-am-500"
                : "text-gray-400" +
                  (isCurrentStep("deactivated") || freezed ? "" : " hover:text-am-500 ");
              let bgColor =
                (isActive ? `bg-${userData.business}-700` : "bg-gray-100") +
                (!isCurrentStep("deactivated") && !freezed
                  ? ` hover:bg-${userData.business}-400 `
                  : "");
              const liCls = idx > 0 ? "md:pl-7" : "";
              const svgFillColor =
                (isActive
                  ? `text-${userData.business}-700`
                  : curr.flg_current
                  ? "text-gray-100"
                  : "text-gray-100") +
                (!isCurrentStep("deactivated") && !freezed
                  ? ` group-hover:text-${userData.business}-400 `
                  : "");

              return (
                <li
                  key={idx}
                  className={
                    `relative px-3 py-8 font-semibold ${textCls} ${bgColor} ${liCls} group flex items-center md:flex-1 ` +
                    (isCurrentStep("deactivated") || freezed
                      ? "pointer-events-none"
                      : "cursor-pointer")
                  }
                  onClick={() =>
                    !loading &&
                    !isCurrentStep("deactivated") &&
                    !freezed &&
                    setSelectedStep(curr)
                  }
                >
                  <span className="flex flex-shrink-0 items-center justify-center">
                    {stepNumber}
                  </span>
                  <div
                    className={`text-xs flex-1 text-center ${
                      curr.flg_active && curr.date
                        ? "ml-3 mr-1 md:text-left"
                        : ""
                    }`}
                  >
                    <div className="uppercase">{name}</div>
                    {curr.id === 1 && (
                      <div className="font-light">
                        <div>
                          {curr.date
                            ? typeof curr.date === "number"
                              ? moment(selectedWorksite.created_at).format(
                                  "DD/MM/YYYY"
                                )
                              : curr.date
                            : ""}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Arrow separator for lg screens and up */}
                  {idx !== arr.length - 1 && (
                    <div
                      className="absolute top-0 -right-5 z-20 hidden h-full w-5 md:block"
                      aria-hidden="true"
                    >
                      <svg
                        className={`h-full w-full fill-current ${svgFillColor}`}
                        viewBox="0 4 24 72"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M0 -2L20 40L0 82"
                          vectorEffect="non-scaling-stroke"
                          stroke="white"
                          strokeWidth="4"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>

      {/* ----- Step change modal ----- */}
      {isModalOpen && (
        <Modal
          type="warning"
          title={intl.formatMessage(
            { id: "confirm_change_status_worksite" },
            {
              newStatus: (
                <span className="font-bold">
                  {selectedStep
                    ? intl
                        .formatMessage({ id: selectedStep.step })
                        .toUpperCase()
                    : "-"}
                </span>
              ),
            }
          )}
          description={
            isCurrentStep("closed") &&
            selectedStep?.step !== "closed" &&
            references?.length > 0
              ? intl.formatMessage({
                  id: "worksite_status_change_reference_warning",
                })
              : null
          }
          onConfirm={dispatchSelectedStep}
          onDismiss={() => {
            setSelectedStep(undefined);
          }}
          onExit={() => {
            setIsModalOpen(false);
            setSelectedStep(undefined);
          }}
          fixed={true}
        />
      )}
    </>
  );
}
